'use client';

import { useRouter } from 'next/navigation'; // Import useRouter
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { useMutation } from '@apollo/client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ErrorIcon } from '@/components/icons/ErrorIcon';
import { FormInputField } from '@/components/form-input-field';
import { SEND_REQUEST_APPOINTMENT_FORM } from '@/utils/constants';
import { sendGTMEvent } from '@next/third-parties/google';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpIcon } from '@/components/icons/HelpIcon';
import { Input } from '@/components/ui/input';
import { trimWhitespaces } from '@/lib/utils';

const formSchema = z
  .object({
    isSubProducer: z.boolean().optional(),
    agencyFullName: z.string().optional(),
    agencyWritingCode: z.string().optional(),
    firstName: z
      .string()
      .min(2, {
        message: 'First name must be at least 2 characters long',
      })
      .regex(/^[A-Za-z\s'-]+$/, 'Please enter a valid first name'),
    lastName: z
      .string()
      .min(2, {
        message: 'Last name must be at least 2 characters long',
      })
      .regex(/^[A-Za-z\s'-]+$/, 'Please enter a valid last name'),
    email: z
      .string()
      .min(1, 'Email is required')
      .email('This email is not valid.'), 
    phoneNumber: z
      .string()
      .min(1, { message: 'Mobile/cell number is required' })
      .regex(/^[0-9]*$/, 'Please enter a valid Mobile/cell number')
      .length(10, { message: 'Please enter a valid Mobile/cell number' }),

    ssn: z.boolean().refine((value) => value === true, {
      message: 'SSN/TIN is required.',
    }),
    token: z.string().min(1, 'Error has occurred. Please try again.'),
  })
  .superRefine((values, ctx) => {
    if (values.isSubProducer) {
      if (!values.agencyFullName || values.agencyFullName.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['agencyFullName'],
          message: 'Agency full name is required.',
        });
      } else if (
        !/^[A-Za-z0-9 ]+(-[A-Za-z0-9 ]+)*$/.test(values.agencyFullName)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['agencyFullName'],
          message: 'Please enter a valid agency full name',
        });
      }
      if (!values.agencyWritingCode || values.agencyWritingCode.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['agencyWritingCode'],
          message: 'Agency writing code is required.',
        });
      } else if (
        !/^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/.test(values.agencyWritingCode)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['agencyWritingCode'],
          message: 'Please enter a valid agency writing code',
        });
      }
    }
  });

export function RequestAppointmentForm() {
  const ssnRef = useRef<any>();
  const [ssnFocused, setSsnFocused] = useState(false);
  const [requestAppointmentForm] = useMutation(SEND_REQUEST_APPOINTMENT_FORM);
  const router = useRouter();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      email: '',
      lastName: '',
      phoneNumber: '',
      ssn: false,
      isSubProducer: true,
      agencyFullName: '',
      agencyWritingCode: '',
      token: '',
    },
  });

  const handleMessage = useCallback(
    (event: any) => {
      const messageEvent = event as any;
      const { type, message } = messageEvent.data ?? {};
      switch (type) {
        case 'invalidProtectedField':
          form.setValue('ssn', false);
          form.setError('ssn', {
            type: 'manual',
            message: message,
          });
          break;
        case 'saveErrorProtectedField':
          break;
        case 'validField':
          form.setValue('ssn', true);
          form.clearErrors('ssn');
          form.setValue('token', message);
          form.clearErrors('token');
          break;
        case 'focusProtectedField':
          setSsnFocused(true);
          break;
        case 'blurProtectedField':
          setSsnFocused(false);
          break;
      }
    },
    [form, setSsnFocused]
  );

  // Add the iframe to the DOM when the component mounts
  useEffect(() => {
    if (ssnRef.current) {
      if (!ssnRef.current.querySelector('#protected-input')) {
        const iframe = document.createElement('iframe');
        iframe.id = 'protected-input';
        iframe.src = '/protected-fields';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.style.display = 'block';
        iframe.tabIndex = 3;
        ssnRef.current.style.position = 'relative';
        ssnRef.current.style.width = '100%';
        ssnRef.current.appendChild(iframe);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [ssnRef, handleMessage]);

  async function onSubmit({
    firstName,
    lastName,
    email,
    phoneNumber,
    isSubProducer,
    agencyFullName,
    agencyWritingCode,
    token,
  }: z.infer<typeof formSchema>) {
    try {
      await requestAppointmentForm({
        variables: {
          requestAppointmentForm: {
            firstName,
            lastName,
            email,
            phoneNumber,
            individualBroker: !isSubProducer,
            agencyFullName,
            agencyWritingCode: isSubProducer ? agencyWritingCode : '',
            token,
          },
        },
      });
    } catch (err) {
      sendGTMEvent({
        event: 'custom_event',
        event_name: 'appointment_request_failed',
      });
      form.setError('root', {
        message:
          'Whoops! Something went wrong with sending the appointment request email! Please try again later.',
      });
      return;
    }
    sendGTMEvent({
      event: 'custom_event',
      event_name: 'appointment_request_success',
    });
    await router.push('/request-appointment/request-submitted');
    form.reset();
  }
  return (
    <div>
      <div className="pb-9">
        <h1 className="heading-xl">
          Request for appointment to sell individual accident and health
          products
        </h1>
        <h3 className="body-lg">
          Please fill out the following to request an appointment to sell
          Guardian individual accident and health products (Dental, Vision,
          Accident). All data is required unless otherwise indicated. Your data
          will be encrypted.
        </h3>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid grid-cols-2 gap-x-8 gap-y-4"
        >
          <div className="flex flex-col gap-4 col-span-1">
            <FormInputField
              registerName="firstName"
              label="First name"
              type="text"
              tabIndex={1}
              onBlur={(e) => {
                form.setValue('firstName', trimWhitespaces(e.target.value));
              }}
            />
            <FormInputField
              registerName="email"
              label="Email"
              type="text"
              tabIndex={3}
              onBlur={(e) => {
                form.setValue('email', trimWhitespaces(e.target.value));
              }}
            />
          </div>
          <div className="flex flex-col gap-4 col-span-1">
            <FormInputField
              registerName="lastName"
              label="Last name"
              type="text"
              tabIndex={2}
              onBlur={(e) => {
                form.setValue('lastName', trimWhitespaces(e.target.value));
              }}
            />
            <FormItem>
              <FormLabel htmlFor="ssn" className="flex gap-2">
                TIN or SSN
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="">
                      <HelpIcon />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Required for appointment. Your information will be
                        encrypted.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </FormLabel>
              <FormField
                name="ssn"
                render={({ field }) => (
                  <FormItem className="pt-3">
                    <input type="text" className="hidden" />
                    <FormControl>
                      <Input
                        id={field.name}
                        asDiv
                        ref={ssnRef}
                        focused={ssnFocused}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormItem>
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-x-8">
             <FormInputField
              registerName="phoneNumber"
              label="Mobile/cell number"
              type="tel"
              tabIndex={2}
              onBlur={(e) => {
                form.setValue('phoneNumber', trimWhitespaces(e.target.value));
              }}
            />
            <FormField
              name="isSubProducer"
              control={form.control}
              render={({ field }) => (
                <FormItem className="pt-3">
                  <div className="flex flex-col">
                    <FormLabel htmlFor="isSubProducer">
                      Subproducer for an Agency?
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-center">
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          tabIndex={5}
                        />
                        <span className="ml-2">
                          {field.value ? (
                            <FormLabel>Yes</FormLabel>
                          ) : (
                            <FormLabel>No</FormLabel>
                          )}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-x-8">
            <FormInputField
              registerName="agencyFullName"
              label="Agency full name"
              type="text"
              disabled={!form.watch('isSubProducer')}
              tabIndex={6}
              onBlur={(e) => {
                form.setValue(
                  'agencyFullName',
                  trimWhitespaces(e.target.value)
                );
              }}
            />
            <FormInputField
              registerName="agencyWritingCode"
              label="Agency writing code"
              type="text"
              disabled={!form.watch('isSubProducer')}
              tabIndex={7}
              onBlur={(e) => {
                form.setValue(
                  'agencyWritingCode',
                  trimWhitespaces(e.target.value)
                );
              }}
            />
          </div>
          <div className="col-span-2 flex items-center justify-start mt-2">
            <Button
              type="submit"
              tabIndex={8}
              data-ta="appointment-form-submit"
              disabled={form.formState.isSubmitting}
            >
              Submit
            </Button>
            <div className="ml-4">
              {Object.keys(form.formState.errors).length > 0 && (
                <FormMessage className="text-error">
                  <ErrorIcon />
                  {'Please complete the missing fields.'}
                </FormMessage>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default RequestAppointmentForm;
